import * as React from "react"
import { CreditCard, Plug, Send } from "lucide-react"
import { Button } from "@/components/ui/button"
import { useBookingT } from "@/hooks/use-translations"
import { fetchConnections, fetchHrisAccess, type HrisAccessReason, type HrisConnection } from "@/features/integrations/hris/hrisApi"
import { StatusBadge } from "./HrisStatusCard"

/** Fired by the HR panel whenever its connection changes, so the header follows. */
export const HRIS_CONNECTION_EVENT = "hris:connection-changed"

export const HRIS_PANEL_ANCHOR = "hris-panel"

type Props = {
  provider: string
  providerName: string
  onRequest: () => void
}

/**
 * The page header's action for an HR system we connect ourselves: its status
 * once connected, "Connect" when the caller may connect it, "Activate the
 * plan" when the plan lacks HR integrations. Asking us is left only for a
 * company the rollout has not reached; a director's colleague or our own staff
 * get no button, the explanation is on the page.
 */
export type HrisHeaderState = { reason: HrisAccessReason | null; connection: HrisConnection | null } | null

/** Whether the caller may connect this HR system and its connection, kept fresh by HRIS_CONNECTION_EVENT. */
export function useHrisHeaderState(provider: string): HrisHeaderState {
  const [state, setState] = React.useState<HrisHeaderState>(null)

  const load = React.useCallback(async () => {
    if (!provider) return
    try {
      const access = await fetchHrisAccess()
      if (!access.allowed) {
        setState({ reason: access.reason, connection: null })
        return
      }
      const { connections } = await fetchConnections()
      setState({ reason: null, connection: connections.find((c) => c.provider === provider) ?? null })
    } catch {
      setState({ reason: "role", connection: null })
    }
  }, [provider])

  React.useEffect(() => {
    void load()
    const reload = () => void load()
    window.addEventListener(HRIS_CONNECTION_EVENT, reload)
    return () => window.removeEventListener(HRIS_CONNECTION_EVENT, reload)
  }, [load])

  return state
}

export function HrisHeaderAction({ state, providerName, onRequest }: Omit<Props, "provider"> & { state: HrisHeaderState }) {
  const t = useBookingT()
  if (state === null) return null

  if (state.connection) {
    return (
      <span className="flex shrink-0 items-center gap-2 text-sm text-neutral-800">
        <span>{t("hris_header_connected", "Connected")}</span>
        <StatusBadge connection={state.connection} />
      </span>
    )
  }

  if (state.reason === null) {
    return (
      <Button
        className="shrink-0"
        onClick={() => document.getElementById(HRIS_PANEL_ANCHOR)?.scrollIntoView({ behavior: "smooth", block: "start" })}
      >
        <Plug className="size-4" />
        {t("hris_header_connect", "Connect {name}").replace("{name}", providerName)}
      </Button>
    )
  }

  if (state.reason === "plan") {
    return (
      <Button className="shrink-0" asChild>
        <a href="/app/subscription">
          <CreditCard className="size-4" />
          {t("hris_header_activate_plan", "Activate the plan to connect")}
        </a>
      </Button>
    )
  }

  if (state.reason === "rollout") {
    return (
      <Button variant="outline" className="shrink-0" onClick={onRequest}>
        <Send className="size-4" />
        {t("integrations_btn_request", "Request integration")}
      </Button>
    )
  }

  return null
}
